import React, { useState, useEffect } from "react";
import Tippy from "@tippyjs/react";

import ModelAccessModal from "../../components/Modals/ModelAccessModal";
import ScannerSettingsModal from "../../components/Modals/ScannerSettingsModal";
import { longDateTime } from "../../utils/DateHelper";
import { useHistory } from "react-router-dom";
import { Collapse } from "react-collapse";
import ActionsDropdown from "../Dropdown/ActionsDropdown";
import { getRoles } from "../../utils/OktaHelpers";
import {
    BOOLEAN_DROPDOWN,
    MAX_PARAMETER,
    ROLES,
    SORTING_DIRECTION_OPTIONS,
} from "../../utils/constants";
import { isAuthorized } from "../../utils/RolesHelpers";
import { useTranslation } from "react-i18next";
import ModelsAPI from "../../API/models.api";
import LookupsAPI from "../../API/lookups.api";
import LazyImage from "../Shared/LazyImage";
import { isEmpty } from "lodash";
import { getEncodedValue, toCamelCase } from "../../utils/Helpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";
import BaseAlertModal from "../Modals/BaseAlertModal";
import BaseModal from "../Modals/BaseModal";
import ModelSupportedFeatures from "../PublishModelSteps/ModelSupportedFeatures";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { getSortDirectionIcon, toggleSort } from "../../utils/FilterHelpers";
import { validate } from "../../utils/ValidationHelpers";
import modelSchema from "../../schemas/model.schema";

const ModelTable = ({
    headers,
    subHeaders,
    rows,
    handleDelete,
    handleDeploy,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    currentPage,
    currentUser,
    getData,
}) => {
    const [isOpened, setIsOpened] = useState({});
    const [deleteButton, setDeleteButton] = useState(false);
    const [selectedModel, setSelectedModel] = useState({});
    const [modelAccessModal, setModelAccessModal] = useState(false);
    const [scannerSettingsModal, setScannerSettingsModal] = useState(false);
    const [modelOptionsModal, setModelOptionsModal] = useState(false);
    const [modelOrganizationAccess, setModelOrganizationAccess] = useState([]);
    const [modelUserAccess, setModelUserAccess] = useState([]);
    const [modelData, setModelData] = useState({});
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [creators, setCreators] = useState({});
    const [modelOptions, setModelOptions] = useState({
        supportedAccessories: [],
        supportedDevices: [],
        enforceOutlierDetection: false,
        isDynamicSorting: false,
        sortingDirection: SORTING_DIRECTION_OPTIONS.ASC,
        isSortingByValues: BOOLEAN_DROPDOWN.true,
        dynamicSortedParameterNames: [],
    });
    const [modelOptionsErrors, setModelOptionsErrors] = useState({});
    const [isModelOptionsValid, setIsModelOptionsValid] = useState(false);
    const [isFormSubmitted, setisFormSubmitted] = useState(false);
    const [isModelOptionsSubmiting, setIsModelOptionsSubmiting] =
        useState(false);
    const [roles, setRoles] = useState([]);
    const history = useHistory();
    const { t } = useTranslation();

    const authRoles = [ROLES.superAdmin, ROLES.support];
    useEffect(() => {
        setRoles(getRoles());
    }, []);

    useEffect(() => {
        setIsOpened({});
    }, [currentPage]);

    useEffect(() => {
        validate(
            {
                ...modelOptions,
                dynamicSortedParameterNames:
                    modelOptions.dynamicSortedParameterNames.filter(
                        (option) => !option.isDeleted
                    ),
            },
            modelSchema.ModelOptionsSchema,
            setIsModelOptionsValid,
            setModelOptionsErrors
        );
    }, [modelOptions]);

    const handleDeleteModel = (row) => {
        setDeleteButton(true);
        setSelectedModel(row);
    };

    const handleDeployModel = (subRow, group) => {
        if (subRow.status.toLowerCase() !== "in progress") {
            handleDeploy(group, subRow);
        }
    };

    const handleConfirmDelete = async () => {
        return handleDelete(selectedModel);
    };

    const isStatusInProgress = (status) => {
        if (status) return status.toLowerCase() === "in progress";
    };

    const handleEditModel = (model, group) => {
        if (model.status.toLowerCase() !== "in progress") {
            const formattedParameterName = getEncodedValue(model.parameterName);
            history.push({
                pathname: `/models/view/${formattedParameterName}`,
                state: {
                    modelParameterId: model.id,
                    modelUserAccesses: group.modelUserAccesses,
                },
            });
        }
    };

    const handleDownloadModelData = async (model) => {
        const url = await ModelsAPI.getDownloadableModelData(model.id);
        if (!isEmpty(url)) {
            Object.assign(document.createElement("a"), {
                target: "hiddenIframe",
                href: url,
                rel: "noreferrer",
            }).click();
        }
    };

    const fetchUserAccess = async (modelId) => {
        if (modelId) {
            return LookupsAPI.getAccessByModelId(modelId);
        }
    };

    const handleModalClick = (data) => {
        setModelData(data);
        setIsModalLoading(true);
        fetchUserAccess(data.id)
            .then((modelAccess) => {
                setModelUserAccess(modelAccess?.modelUserAccesses);
                setModelOrganizationAccess(
                    modelAccess?.modelOrganizationAccesses
                );
            })
            .catch((error) => {
                LogCapture.error(
                    `Couldn't fetch user access for model: ${data.modelName}, error: ${error}`
                );
            })
            .finally(() => setIsModalLoading(false));
    };

    const handleUpload = (model, group) => {
        history.push({
            pathname: "/models/upload",
            state: {
                data: {
                    ...model,
                    materialName: group.materialName,
                    modelName: group.modelName,
                    createdAt: group.createdAt,
                    createdBy: group.createdBy,
                },
                modelType: model.modelType,
            },
        });
    };

    const handleModelAccessModal = (row) => {
        setModelAccessModal(true);
        handleModalClick(row);
    };

    const mapModelParameters = (row, parametersWithUnits) => {
        return row?.modelParameters
            ?.filter((parameter) => parameter.isDynamicSorted)
            .map((parameter) => ({
                label: parameter.parameterName,
                value: parameter.id,
                unitName: parametersWithUnits.find(
                    (parameterWithUnit) =>
                        parameterWithUnit.label === parameter.parameterName
                )?.unitName,
            }));
    };

    const handleModelOptionsModal = async (row) => {
        setModelData(row);
        const parametersWithUnits = await getParametersWithUnit(row.id);
        setModelOptions({
            supportedAccessories: row.supportedAccessories,
            supportedDevices: row.supportedDevices,
            enforceOutlierDetection: row.enforceOutlierDetection,
            isDynamicSorting: row.isDynamicSorting,
            isSortingByValues: BOOLEAN_DROPDOWN[row.isSortingByValues],
            sortingDirection: SORTING_DIRECTION_OPTIONS[row.sortingDirection],
            dynamicSortedParameterNames: mapModelParameters(
                row,
                parametersWithUnits
            ),
            parameters: parametersWithUnits,
        });
        setModelOptionsModal(true);
    };

    const getParametersWithUnit = async (modelId) => {
        return await LookupsAPI.getParametersWithUnit(modelId);
    };

    const handleModelOptionsSubmit = () => {
        setisFormSubmitted(true);
        if (isModelOptionsValid) {
            setIsModelOptionsSubmiting(true);
            handleUpdateModel();
        }
    };

    const handleUpdateModel = () => {
        const modeldata = {
            id: modelData.id,
            image: modelData.image,
            supportedAccessories: modelOptions.supportedAccessories.map(
                (v) => ({
                    id: v.value,
                    isDeleted: v.isDeleted === true,
                })
            ),
            supportedDevices: modelOptions.supportedDevices.map((v) => ({
                id: v.value,
                isDeleted: v.isDeleted === true,
            })),
            enforceOutlierDetection: modelOptions.enforceOutlierDetection,
            isDynamicSorting: modelOptions?.isDynamicSorting,
            sortingDirection:
                modelOptions?.sortingDirection?.value ||
                SORTING_DIRECTION_OPTIONS.ASC.value,
            isSortingByValues: modelOptions?.isSortingByValues?.value,
            dynamicSortedParameterNames:
                modelOptions?.dynamicSortedParameterNames
                    .filter((a) => !a.isDeleted)
                    .map((a) => a.label),
        };

        ModelsAPI.updateModel(modeldata)
            .then((res) => {
                if (!isEmpty(res)) {
                    getData();
                    SuccessAlert(t("editModelSuccessMessage"));
                    LogCapture.info(
                        `Model updated with id: ${modelData?.modelId}`,
                        ENTITIES.models,
                        ACTIONS.models.updateModel
                    );
                    setModelOptionsModal(false);
                } else {
                    LogCapture.error(
                        `Couldn't update model with id: ${modelData?.modelId}`
                    );
                }
            })
            .catch((error) => {
                console.log(error);
                LogCapture.error(
                    `Couldn't update model with id: ${modelData?.modelId}, error: ${error}`
                );
                ErrorAlert(t("editModelFailMessage"));
            })
            .finally(() => {
                setIsModelOptionsSubmiting(false);
            });
    };

    const handleScannerSettingsModal = (row) => {
        setScannerSettingsModal(true);
        handleModalClick(row);
    };

    const renderHeadersData = () => {
        return (
            headers &&
            headers.map((header, index) => {
                if (!header.roles) {
                    return (
                        <th
                            key={index}
                            scope="col"
                            className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                        >
                            <div className="flex items-center justify-center">
                                {!header.skipSort && (
                                    <i
                                        onClick={() =>
                                            toggleSort(
                                                header.name,
                                                sortBy,
                                                setSortBy,
                                                sortDirection,
                                                setSortDirection
                                            )
                                        }
                                        className={`fas ${getSortDirectionIcon(
                                            header.name,
                                            sortBy,
                                            sortDirection
                                        )} table-head-icon`}
                                    />
                                )}
                                <span className="ml-2">{header.label}</span>
                            </div>
                        </th>
                    );
                }
            })
        );
    };

    const renderSubHeadersData = () => {
        return (
            subHeaders &&
            subHeaders.map((header, index) => {
                return (
                    <th
                        key={index}
                        scope="col"
                        className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                    >
                        <div className="flex items-center justify-center">
                            <span>{header}</span>
                        </div>
                    </th>
                );
            })
        );
    };

    const handleExpand = (key) => {
        let opened = { ...isOpened };
        opened[key] = !isOpened[key];
        setIsOpened(opened);
    };

    const getSubRowActions = (row, subRow) => {
        return [
            {
                name: t("view"),
                icon: "fas fa-eye",
                handler: handleEditModel,
                authorized: true,
                isDisabled: (status) => isStatusInProgress(status),
            },
            {
                name: subRow.deployed ? t("undeploy") : t("deploy"),
                icon: subRow.deployed ? "fas fa-arrow-down" : "fas fa-arrow-up",
                handler: handleDeployModel,
                authorized:
                    isAuthorized(
                        [
                            ROLES.support,
                            ROLES.partnerAdmin,
                            ROLES.partnerUser,
                            ROLES.admin,
                            ROLES.user,
                        ],
                        roles
                    ) &&
                    !isAuthorized([ROLES.support, ROLES.partnerAdmin], roles)
                        ? row?.write
                            ? true
                            : false
                        : true,
                isDisabled: (status) => isStatusInProgress(status),
            },
            {
                name: t("upload"),
                icon: "fas fa-upload",
                handler: handleUpload,
                authorized:
                    isAuthorized(authRoles, roles) ||
                    (isAuthorized([ROLES.partnerAdmin], roles) &&
                        currentUser?.organization?.uploadModelAccess),
            },
            {
                name: t("delete"),
                icon: "fas fa-trash",
                handler: handleDeleteModel,
                authorized: isAuthorized(
                    [ROLES.support, ROLES.partnerAdmin],
                    roles
                ),
                color: "red",
                includeHorizontalLine: true,
            },
        ];
    };

    const getRowActions = (row) => {
        return [
            {
                name: t("editAccess"),
                icon: "fas fa-users-cog",
                handler: handleModelAccessModal,
                authorized: isAuthorized(
                    [ROLES.support, ROLES.partnerAdmin],
                    roles
                ),
            },
            {
                name: t("editModelOptions"),
                icon: "fas fa-cog",
                handler: handleModelOptionsModal,
                authorized: isAuthorized(
                    [ROLES.support, ROLES.partnerAdmin],
                    roles
                ),
            },
            {
                name: t("addParameter"),
                icon: "fas fa-plus",
                isDisabled: () => row?.modelParameters?.length >= MAX_PARAMETER,
                handler: () => {
                    history.push({
                        pathname: "/models/parameter/create",
                        state: {
                            modelId: row.id,
                            modelType: row.type,
                        },
                    });
                },
                authorized: isAuthorized(
                    [ROLES.support, ROLES.partnerAdmin],
                    roles
                ),
            },
            {
                name: t("scannerSettings"),
                icon: "fas fa-tools",
                handler: handleScannerSettingsModal,
                authorized: isAuthorized(
                    [ROLES.support, ROLES.partnerAdmin],
                    roles
                ),
            },
            {
                name: t("download"),
                icon: "fas fa-download",
                handler: handleDownloadModelData,
                authorized: isAuthorized(authRoles, roles),
                isDisabled: () => row.isCustom,
            },
            {
                name: t("clone"),
                icon: "fas fa-clone",
                handler: handleCloneModel,
                authorized: isAuthorized([ROLES.superAdmin], roles),
            },
        ];
    };

    const handleCloneModel = (selectedModel) => {
        history.push({
            pathname: "/models/clone",
            state: {
                selectedModel,
            },
        });
    };

    const renderStatusLanguage = (status) => {
        return t(toCamelCase(status));
    };

    const renderSubRowsData = (row) => {
        return (
            row.modelParameters &&
            row.modelParameters.map((subRow) => {
                return (
                    <tr key={subRow.id} className="tr-ele">
                        <td className="px-6 py-3 whitespace-wrap">
                            {subRow.parameterName}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {subRow.version || "---"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {subRow.publishedAt
                                ? longDateTime(subRow.publishedAt)
                                : "---"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            <div className="flex items-center justify-center">
                                <div
                                    className={`tag-ele tag-${subRow.status.toLowerCase()} px-2`}
                                >
                                    {renderStatusLanguage(subRow.status) ||
                                        subRow.status}
                                </div>
                            </div>
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            <div className="actions-wrappers">
                                <ActionsDropdown
                                    row={row}
                                    subRow={subRow}
                                    attributes={getSubRowActions(row, subRow)}
                                    selected={selectedModel}
                                    entity="parameters"
                                />
                            </div>
                        </td>
                    </tr>
                );
            })
        );
    };

    const renderRowsData = () => {
        return (
            rows &&
            rows.map((row, index) => {
                return (
                    <React.Fragment key={`${row.id}-${index}`}>
                        <tr
                            className={`tr-ele cursor-pointer ${
                                index % 2 === 0 ? "even" : "odd"
                            }`}
                            onClick={() => handleExpand(row.id)}
                        >
                            <td className="px-6 py-3 whitespace-wrap">
                                <div className="flex items-center mr-10 ">
                                    <i
                                        className={`fas fa-caret-${
                                            isOpened[row.id] ? "down" : "right"
                                        } mr-2`}
                                    ></i>
                                    <LazyImage
                                        src={
                                            row.image ||
                                            require("../../assets/images/neural.png")
                                                .default
                                        }
                                        className="table-user-img"
                                        alt="UserImage"
                                    />
                                    <span className="user-name-area text-left">
                                        {row.modelName}
                                    </span>
                                </div>
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {row.materialName}
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {longDateTime(row.createdAt)}
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {
                                    <Tippy
                                        content={row.createdBy}
                                        placement="top"
                                        animation="scale"
                                    >
                                        <span
                                            tabIndex="0"
                                            className="description-oneline tooltip-container"
                                        >
                                            {row.createdBy}
                                        </span>
                                    </Tippy>
                                }
                            </td>
                            {isAuthorized([ROLES.superAdmin], roles) && (
                                <td className="px-6 py-3 whitespace-wrap">
                                    {row.organization || "---"}
                                </td>
                            )}
                            {isAuthorized(
                                [
                                    ROLES.support,
                                    ROLES.partnerAdmin,
                                    ROLES.superAdmin,
                                ],
                                roles
                            ) && (
                                <td className="px-6 py-3 whitespace-wrap">
                                    <div className="actions-wrappers">
                                        <ActionsDropdown
                                            row={row}
                                            selected={selectedModel}
                                            attributes={getRowActions(row)}
                                            entity="models"
                                        />
                                    </div>
                                </td>
                            )}
                        </tr>
                        <tr hidden={!isOpened[row.id]} key={`inner-${index}`}>
                            <td colSpan={headers.length} className="bg-white">
                                <Collapse isOpened={isOpened[row.id]}>
                                    <div className="table-responsive overflow-hidden border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y px-8 pb-4">
                                        <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center">
                                            <thead>
                                                <tr className="bg-white">
                                                    {renderSubHeadersData()}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {renderSubRowsData(row)}
                                            </tbody>
                                        </table>
                                    </div>
                                </Collapse>
                            </td>
                        </tr>
                    </React.Fragment>
                );
            })
        );
    };

    return (
        <>
            <div className="flex flex-col mb-5">
                <div className="table-responsive action-wrapper shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                    <table className="table-striped custom min-w-full divide-y divide-gray-200 text-xs text-center">
                        <thead className="parent-head">
                            <tr>{renderHeadersData()}</tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {renderRowsData()}
                        </tbody>
                    </table>
                </div>
            </div>
            <BaseAlertModal
                isVisible={deleteButton}
                setIsVisible={setDeleteButton}
                title={t("deleteParameterTitle", {
                    parameterName: selectedModel?.parameterName,
                })}
                message={t("deleteParameterMessage")}
                action={{ submitAction: handleConfirmDelete }}
                type="delete"
            />
            {isAuthorized([ROLES.support, ROLES.partnerAdmin], roles) && (
                <>
                    <ModelAccessModal
                        isVisible={modelAccessModal}
                        setIsVisible={setModelAccessModal}
                        modelUserAccess={modelUserAccess}
                        modelOrganizationAccess={modelOrganizationAccess}
                        createdBy={modelData?.createdBy}
                        data={modelData}
                        isLoading={isModalLoading}
                        creatorsDictionary={creators}
                        creatorsDictionarySetter={setCreators}
                    />
                    <ScannerSettingsModal
                        isVisible={scannerSettingsModal}
                        setIsVisible={setScannerSettingsModal}
                        data={modelData}
                    />
                    <BaseModal
                        isVisible={modelOptionsModal}
                        setIsVisible={setModelOptionsModal}
                        title={t("editModelOptions")}
                        action={{
                            name: t("edit"),
                            submitAction: handleModelOptionsSubmit,
                        }}
                        isSubmitting={isModelOptionsSubmiting}
                    >
                        <ModelSupportedFeatures
                            modelOptions={modelOptions}
                            setModelOptions={setModelOptions}
                            isEdit={true}
                            errors={modelOptionsErrors}
                            isFormSubmitted={isFormSubmitted}
                        />
                    </BaseModal>
                </>
            )}
        </>
    );
};

export default ModelTable;
